/**
 * Stage Distribution Service
 * Fetches stage distribution data with year-over-year grouping
 */

// Color palette removed - using hand-picked professional colors instead

export interface StageDistributionYearData {
  funding: number;
  deals: number;
  pct: number;
}

export interface StageDistributionData {
  [year: string]: {
    [stage: string]: StageDistributionYearData;
  };
}

export interface StageDistributionResponse {
  success: boolean;
  data: StageDistributionData;
  years: string[];
  stages: string[];
  metadata: {
    timeframe: string;
    filters_applied: boolean;
    current_period: {
      start: string;
      end: string;
    };
  };
  cache_status?: string;
  response_time_ms?: number;
}

class StageDistributionService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';
  }

  /**
   * Fetch stage distribution data
   */
  async getStageDistribution(
    timeframe: string = '12w',
    weekOffset: number = 0,
    filters?: Record<string, string[] | undefined>
  ): Promise<StageDistributionResponse> {
    const params = new URLSearchParams({
      timeframe,
      weekOffset: weekOffset.toString()
    });

    // Add filters to query params
    if (filters) {
      Object.entries(filters).forEach(([key, values]) => {
        if (values && values.length > 0) {
          params.set(key, values.join(','));
        }
      });
    }

    const url = `${this.baseUrl}/api/v2/stages/distribution?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: StageDistributionResponse = await response.json();

      if (!data.success) {
        throw new Error('API returned unsuccessful response');
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch stage distribution:', error);
      throw error;
    }
  }

  /**
   * Get stage display name (human-friendly)
   */
  getStageName(stage: string): string {
    // Display names match backend exactly
    return stage;
  }

  /**
   * Get stage color for consistent visualization
   * Uses earth tones with strategic greens and reds for standout
   * Professional, muted colors that are easy on the eyes
   */
  getStageColor(stage: string): string {
    // Earth tone palette with strategic reds/greens
    const STAGE_COLORS: Record<string, string> = {
      'Pre-Seed': '#A8DADC',      // Pale teal - earliest stage
      'Seed': '#7BA05B',          // Olive green
      'Series A': '#2D6A4F',      // Forest green - growth standout
      'Series B': '#C77C3A',      // Burnt orange
      'Series C': '#8B7355',      // Warm brown
      'Growth Stage': '#C9485B',  // Burgundy red - growth standout
      'Series D': '#9B2335',      // Wine red
      'Series E+': '#5A4A3A',     // Dark brown
      'Post-IPO': '#3D3D3D',      // Charcoal - mature stage
      'Other': '#6B7280'          // Gray - non-standard stages
    };

    return STAGE_COLORS[stage] || '#64748B'; // Slate-500 fallback
  }
}

export const stageDistributionService = new StageDistributionService();
